import React, { useEffect, useMemo, useState } from 'react';
import './css/AgePricingEditor.css';

const WEEKDAYS = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

const DAY_LABELS = {
  MONDAY: 'Mon',
  TUESDAY: 'Tue',
  WEDNESDAY: 'Wed',
  THURSDAY: 'Thu',
  FRIDAY: 'Fri',
  SATURDAY: 'Sat',
  SUNDAY: 'Sun',
};

const MODE_OPTIONS = [
  { value: 'REGULAR', label: 'Regular (ASAP)' },
  { value: 'FIXED', label: 'Fixed slots only' },
  { value: 'BOTH', label: 'Both regular + fixed' },
];

const DEFAULT_SLOT_DURATION_MINUTES = 30;

export function emptyDeliverySlots() {
  return WEEKDAYS.reduce((acc, day) => {
    acc[day] = [];
    return acc;
  }, {});
}

export function normalizeDeliverySlots(payload) {
  const base = emptyDeliverySlots();
  if (!payload || typeof payload !== 'object') return base;
  WEEKDAYS.forEach((day) => {
    const times = payload[day] ?? payload[day.toLowerCase()] ?? [];
    if (!Array.isArray(times)) {
      base[day] = [];
      return;
    }
    base[day] = [
      ...new Set(
        times
          .map((t) => String(t || '').slice(0, 5))
          .filter((t) => /^\d{2}:\d{2}$/.test(t)),
      ),
    ].sort();
  });
  return base;
}

export function normalizeSlotDurationMinutes(value, fallback = DEFAULT_SLOT_DURATION_MINUTES) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  if (n < 15) return 15;
  if (n > 180) return 180;
  return Math.round(n);
}

export function addMinutesToTime24(time24, minutes) {
  const match = String(time24 || '').match(/^(\d{2}):(\d{2})$/);
  if (!match) return '';
  const total =
    (Number(match[1]) * 60 + Number(match[2]) + Number(minutes || 0) + 24 * 60) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

export function formatSlotWindow(startTime, durationMinutes = DEFAULT_SLOT_DURATION_MINUTES) {
  const start = String(startTime || '').slice(0, 5);
  if (!/^\d{2}:\d{2}$/.test(start)) return start || '';
  const end = addMinutesToTime24(start, durationMinutes);
  return end ? `${start} - ${end}` : start;
}

/**
 * Weekday fixed delivery times (no cutoff) + delivery mode.
 * Optional party prep fields for restaurants.
 * Optional slot duration (store): start times shown as windows.
 */
export default function DeliverySlotsEditor({
  deliveryMode = 'REGULAR',
  deliverySlots,
  onModeChange,
  onSlotsChange,
  disabled = false,
  showPartyPrep = false,
  partyPrepBaseMinutes = 45,
  partyPrepMinutesPerPack = 2,
  onPartyPrepChange,
  showSlotDuration = false,
  slotDurationMinutes = DEFAULT_SLOT_DURATION_MINUTES,
  onSlotDurationChange,
  title = 'MYEATS delivery schedule',
  hint = 'Regular = ASAP ETA. Fixed = show next available slot (no order cutoff). Both = customer can use either.',
}) {
  const slots = useMemo(
    () => normalizeDeliverySlots(deliverySlots),
    [deliverySlots],
  );
  const duration = normalizeSlotDurationMinutes(slotDurationMinutes);
  const showSlots = deliveryMode === 'FIXED' || deliveryMode === 'BOTH';

  const daysWithSlots = useMemo(
    () => WEEKDAYS.filter((day) => (slots[day] || []).length > 0),
    [slots],
  );
  const emptyDays = useMemo(
    () => WEEKDAYS.filter((day) => (slots[day] || []).length === 0),
    [slots],
  );

  const [copyFromDay, setCopyFromDay] = useState('MONDAY');
  const [copyTargets, setCopyTargets] = useState(() => new Set());

  useEffect(() => {
    if (daysWithSlots.length === 0) {
      return;
    }
    if (!daysWithSlots.includes(copyFromDay)) {
      setCopyFromDay(daysWithSlots[0]);
    }
  }, [daysWithSlots, copyFromDay]);

  useEffect(() => {
    setCopyTargets(new Set(emptyDays));
  }, [emptyDays.join('|')]);

  const updateDayTimes = (day, timesText) => {
    const times = timesText
      .split(/[\s,]+/)
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => (t.length === 4 && t.includes(':') ? `0${t}` : t))
      .filter((t) => /^\d{1,2}:\d{2}$/.test(t))
      .map((t) => {
        const [h, m] = t.split(':');
        return `${String(h).padStart(2, '0')}:${m}`;
      });
    onSlotsChange?.({
      ...slots,
      [day]: [...new Set(times)].sort(),
    });
  };

  const addTime = (day, timeValue) => {
    const t = String(timeValue || '').slice(0, 5);
    if (!/^\d{2}:\d{2}$/.test(t)) return;
    const next = [...new Set([...(slots[day] || []), t])].sort();
    onSlotsChange?.({ ...slots, [day]: next });
  };

  const removeTime = (day, timeValue) => {
    onSlotsChange?.({
      ...slots,
      [day]: (slots[day] || []).filter((t) => t !== timeValue),
    });
  };

  const toggleCopyTarget = (day) => {
    setCopyTargets((prev) => {
      const next = new Set(prev);
      if (next.has(day)) {
        next.delete(day);
      } else {
        next.add(day);
      }
      return next;
    });
  };

  const copyToSelectedEmptyDays = () => {
    const sourceTimes = [...(slots[copyFromDay] || [])];
    if (sourceTimes.length === 0 || copyTargets.size === 0) {
      return;
    }
    const next = { ...slots };
    copyTargets.forEach((day) => {
      if (day === copyFromDay) return;
      if ((slots[day] || []).length > 0) return;
      next[day] = [...sourceTimes];
    });
    onSlotsChange?.(next);
  };

  const fillAllEmptyFromFirst = () => {
    const sourceDay = daysWithSlots[0];
    if (!sourceDay || emptyDays.length === 0) return;
    const sourceTimes = [...(slots[sourceDay] || [])];
    const next = { ...slots };
    emptyDays.forEach((day) => {
      next[day] = [...sourceTimes];
    });
    onSlotsChange?.(next);
  };

  return (
    <div className="age-pricing-editor meal-delivery-editor">
      <div className="age-pricing-editor__header">
        <label className="age-pricing-editor__title">{title}</label>
        <p className="age-pricing-editor__hint">{hint}</p>
      </div>

      <div
        className="age-pricing-editor__table-wrap delivery-slot-options-row"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 24,
          marginBottom: 12,
          alignItems: 'flex-start',
        }}
      >
        <div style={{ width: 260, maxWidth: '100%' }}>
          <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>
            Delivery mode
          </label>
          <select
            value={deliveryMode || 'REGULAR'}
            disabled={disabled}
            onChange={(e) => onModeChange?.(e.target.value)}
            aria-label="Delivery mode"
            style={{ width: '100%' }}
          >
            {MODE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {showSlotDuration ? (
          <div style={{ width: 200, maxWidth: '100%' }}>
            <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>
              Slot duration (minutes)
            </label>
            <input
              type="number"
              min={15}
              max={180}
              step={5}
              disabled={disabled}
              value={duration}
              onChange={(e) =>
                onSlotDurationChange?.(normalizeSlotDurationMinutes(e.target.value, duration))
              }
              aria-label="Slot duration in minutes"
              style={{ width: 120 }}
            />
            <p className="age-pricing-editor__hint" style={{ marginTop: 6 }}>
              Example: {formatSlotWindow('14:00', duration)}
            </p>
          </div>
        ) : null}
      </div>

      {showPartyPrep ? (
        <div
          className="age-pricing-editor__table-wrap"
          style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12 }}
        >
          <label>
            Party prep base (min)
            <input
              type="number"
              min={0}
              disabled={disabled}
              value={partyPrepBaseMinutes}
              onChange={(e) =>
                onPartyPrepChange?.({
                  partyPrepBaseMinutes: e.target.value,
                  partyPrepMinutesPerPack,
                })
              }
              style={{ display: 'block', marginTop: 4 }}
            />
          </label>
          <label>
            Party prep per pack (min)
            <input
              type="number"
              min={0}
              disabled={disabled}
              value={partyPrepMinutesPerPack}
              onChange={(e) =>
                onPartyPrepChange?.({
                  partyPrepBaseMinutes,
                  partyPrepMinutesPerPack: e.target.value,
                })
              }
              style={{ display: 'block', marginTop: 4 }}
            />
          </label>
        </div>
      ) : null}

      {showSlots ? (
        <div className="age-pricing-editor__table-wrap">
          {daysWithSlots.length > 0 && emptyDays.length > 0 && !disabled ? (
            <div
              className="delivery-slot-copy-bar"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 10,
                alignItems: 'center',
                marginBottom: 12,
                padding: '10px 12px',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                background: '#fff',
              }}
            >
              <span style={{ fontWeight: 600, color: '#334155', fontSize: 13 }}>
                Copy to empty days
              </span>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                From
                <select
                  value={copyFromDay}
                  onChange={(e) => setCopyFromDay(e.target.value)}
                  aria-label="Copy slots from day"
                >
                  {daysWithSlots.map((day) => (
                    <option key={day} value={day}>
                      {DAY_LABELS[day]}
                    </option>
                  ))}
                </select>
              </label>
              <div style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 8 }}>
                {emptyDays.map((day) => (
                  <label
                    key={day}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      fontSize: 13,
                      color: '#334155',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={copyTargets.has(day)}
                      onChange={() => toggleCopyTarget(day)}
                    />
                    {DAY_LABELS[day]}
                  </label>
                ))}
              </div>
              <button
                type="button"
                className="age-pricing-editor__add-btn"
                onClick={copyToSelectedEmptyDays}
                disabled={copyTargets.size === 0}
              >
                Copy
              </button>
              <button
                type="button"
                className="age-pricing-editor__add-btn"
                onClick={fillAllEmptyFromFirst}
                title={`Copy ${DAY_LABELS[daysWithSlots[0]]} to every empty day`}
              >
                Fill all empty
              </button>
            </div>
          ) : null}

          <table className="age-pricing-editor__table">
            <thead>
              <tr>
                <th>Day</th>
                <th>
                  {showSlotDuration
                    ? `Delivery windows (${duration} min)`
                    : 'Fixed delivery times (HH:mm)'}
                </th>
                <th>Add start</th>
              </tr>
            </thead>
            <tbody>
              {WEEKDAYS.map((day) => (
                <tr key={day}>
                  <td>{DAY_LABELS[day]}</td>
                  <td>
                    <div
                      className="delivery-slot-chips"
                      style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}
                    >
                      {(slots[day] || []).length === 0 ? (
                        <span style={{ opacity: 0.6, color: '#64748b' }}>None</span>
                      ) : (
                        (slots[day] || []).map((t) => {
                          const label = showSlotDuration
                            ? formatSlotWindow(t, duration)
                            : String(t);
                          return (
                            <span key={t} className="delivery-slot-chip">
                              <span>{label || t}</span>
                              <button
                                type="button"
                                disabled={disabled}
                                onClick={() => removeTime(day, t)}
                                title={`Remove ${label || t}`}
                                aria-label={`Remove ${label || t}`}
                              >
                                ×
                              </button>
                            </span>
                          );
                        })
                      )}
                    </div>
                    <input
                      type="text"
                      disabled={disabled}
                      placeholder="Or paste start times 09:00, 13:00, 18:00"
                      defaultValue={(slots[day] || []).join(', ')}
                      key={`${day}-${(slots[day] || []).join(',')}`}
                      onBlur={(e) => updateDayTimes(day, e.target.value)}
                      style={{ marginTop: 8, width: '100%', color: '#0f172a' }}
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      disabled={disabled}
                      onChange={(e) => {
                        if (e.target.value) {
                          addTime(day, e.target.value);
                          e.target.value = '';
                        }
                      }}
                      aria-label={`Add start time for ${day}`}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

export { WEEKDAYS, MODE_OPTIONS, DEFAULT_SLOT_DURATION_MINUTES };
